import { CommonModule } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from "@angular/material/card";
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from "@angular/material/select";
import { DialogData, SampleFreqReq, StreamSamplingFreq } from '../../../../interfaces/streamrecurringLoad';
import { FeedstockService } from '../../../../services/feedstockservice';
import { IdName } from '../../../../interfaces/pricematrixmodel';
import { SaveStreamSampleFreqRequest } from '../../../../interfaces/generatorsmodel';
import { LookUpService } from '../../../../services/lookupservice';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
  selector: 'app-addedit-samplefreq',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    DragDropModule,
    MatSlideToggleModule
],
  templateUrl: './addedit-samplefreq.component.html',
  styleUrl: './addedit-samplefreq.component.scss'
})
export class AddeditSamplefreqComponent implements OnInit {

  @Input() initialData: StreamSamplingFreq | null = null;
  @Output() save = new EventEmitter<StreamSamplingFreq>();
  @Output() cancel = new EventEmitter<void>();
  public ipdata: SampleFreqReq = inject(MAT_DIALOG_DATA);
  isFormReady = signal(false);
  displayValue = signal<string>('');
  canSave: boolean = false;
  samplingTypes: WritableSignal<IdName[]> = signal([]);
  testingTypes: WritableSignal<IdName[]> = signal([]);
  freqTypeMasterData: WritableSignal<IdName[]> = signal([]);
  durationMasterData: WritableSignal<IdName[]> = signal([]);
  unitMasterData: WritableSignal<IdName[]> = signal([]);
  
  currentSamplingType = signal<number | null>(null);
  currentFreqUnit = signal<number | null>(null);

  thresholdLabel = computed(() => {
    const unitId = this.currentFreqUnit();
    if (unitId) { 
      const unitName = this.unitMasterData().find(item => item.id === unitId)?.name;
      if (unitName && (unitName === 'Tons' || unitName === 'Gallons')) {
        return `Alert Threshold (${unitName})`;
      }
    }
    return 'Alert Threshold (Days)';
  });

  filteredFreqTypes = computed(() => {
    const type = this.currentSamplingType();
    if (type === 1)  return this.durationMasterData();
    if (type === 2) return this.unitMasterData();
    return [];
  });

  sfForm!: FormGroup;

  constructor(public dialogRef: MatDialogRef<AddeditSamplefreqComponent>,
    private fsService: FeedstockService,private _dialog: MatDialog,
    private fb: FormBuilder, private lookup: LookUpService
  ) {

  }

  
  ngOnInit(): void {
    this.canSave = this.ipdata.canSave;
    this.getFreequencyLookups();
  }
  
  private postLookupSetup(): void {
    console.log("sample edit data",this.ipdata.editData);
    const data = this.ipdata.editData || {
      id: 0,
      streamID: null,
      type: null,
      freqType: null,
      freq: null,
      alertThreshold: null,
      testingType: null,
      sampleUpOnFirstDelivery: false
    };

    // 1. Initialize the Form (now that data is loaded)
    this.sfForm = this.fb.group({
      id: new FormControl(data.id), // Hidden
      streamID: new FormControl(this.ipdata.streamId), // Hidden
      type: new FormControl(data.type, Validators.required),
      freqType: new FormControl(data.freqType, Validators.required),
      freq: new FormControl(data.freq, [Validators.required, Validators.min(1)]),
      alertThreshold: new FormControl(data.alertThreshold, [Validators.required, Validators.min(1)]),
      testingType: new FormControl(data.testingType, Validators.required),
      customerid: new FormControl(this.ipdata.genId),
      sampleUpOnFirstDelivery: new FormControl(data.sampleUpOnFirstDelivery ?? false),
    });
    
    this.currentSamplingType.set(data.type ? data.type : null);

    this.sfForm.get('freqType')?.valueChanges.subscribe(freqTypeId => {    
      const samplingType = this.sfForm.get('type')?.value; 
      if (samplingType === 2 && freqTypeId) {
        const selectedFreqItem = this.unitMasterData().find(item => item.id === freqTypeId);
        if (selectedFreqItem) {
          this.currentFreqUnit.set(selectedFreqItem.id);
        }
      } else {
        this.currentFreqUnit.set(null);
      }
    });

    if (data.type === 2 && data.freqType) {
      const initialFreqItem = this.unitMasterData().find(item => item.id === data.freqType);
      this.currentFreqUnit.set(initialFreqItem ? initialFreqItem.id : null);
    }

    this.isFormReady.set(true);
  }
  
  getFreequencyLookups(){
    this.lookup.getSampleFrequencyLookup().subscribe({
      next: async (response) => {
        this.samplingTypes.set(response.filter(item => item.type === 'SchedulingType'));
        this.testingTypes.set(response.filter(item => item.type === 'PermitType'));
        this.durationMasterData.set(response.filter(item => item.type === 'Duration'));
        this.unitMasterData.set(response.filter(item => item.type === 'Unit'));
        this.freqTypeMasterData.set(response.filter(item => item.type === 'Unit' || item.type === 'Duration'));
        this.postLookupSetup();
      }
    })
  }

  onSamplingTypeChange(type: number | null): void {
    if (!this.sfForm) return; 

    this.currentSamplingType.set(type);
    this.sfForm.get('freqType')?.setValue(null, { emitEvent: false });
    this.currentFreqUnit.set(null);
    this.sfForm.get('freqType')?.markAsTouched();
  }

  onSave(): void {
    if (this.sfForm.valid) {
      var payload: SaveStreamSampleFreqRequest = {
        id: this.sfForm.get('id')?.value,
        streamID: this.sfForm.get('streamID')?.value,
        type: this.sfForm.get('type')?.value,
        freqType: this.sfForm.get('freqType')?.value,
        freq: this.sfForm.get('freq')?.value,
        alertThreshold: this.sfForm.get('alertThreshold')?.value,
        testingType: this.sfForm.get('testingType')?.value,
        customerid: this.sfForm.get('customerid')?.value,
        sampleUpOnFirstDelivery: this.sfForm.get('sampleUpOnFirstDelivery')?.value
      };
  
      console.log('sample freq data to save:', payload);
  
      this.fsService.saveSampleFreq(payload).subscribe({
        next: (response) => {
          console.log('Sample Frequency Save successful:', response);
          this.dialogRef.close(true);
        }
      });
    } else {
      this.sfForm.markAllAsTouched();
    }
  }

  closeModal(): void {
    this.dialogRef.close(false);
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number, controlName: string): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    if (maxDecimals === 0) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      val = val.replace(/[^0-9.]/g, '');
    }
    const points = val.split('.');
    if (points.length > 2) {
      val = points[0] + '.' + points.slice(1).join('');
    }
    
    const numericValue = parseFloat(val);
    if (!isNaN(numericValue) && numericValue > maxValue) {
      val = maxValue.toString();
    }
    
    if (val.includes('.')) {
      const [intPart, decimalPart] = val.split('.');
      if (decimalPart.length > maxDecimals) {
        val = `${intPart}.${decimalPart.substring(0, maxDecimals)}`;
      }
    }
    this.displayValue.set(val);
    
    input.value = val;
    const isValueEmpty = val === '' || val === '.';
    const parsedValue = maxDecimals === 0 ? parseInt(val, 10) : parseFloat(val);
    const finalValue = isValueEmpty ? null : parsedValue;
    this.sfForm.get(controlName)?.setValue(finalValue, { emitEvent: false });
  }
}